import React, { useEffect, useMemo, useState } from 'react';
import useStore from '@data/state/zustand';
import translate from '@data/translations';
import rest from '@data/restWrapper';
import InlineModal from '@components/modalInline';
import { ReservationModel, ReservationStatus } from '@common/modelDefinition';

const ACTIVE_STATUSES = [
    ReservationStatus.earlyBird,
    ReservationStatus.confirmed,
    ReservationStatus.checkedIn,
];

interface ChainMove {
    reservationId: number;
    guestName: string;
    fromRoomInfoId: number;
    fromRoomName: string | null;
    toRoomInfoId: number;
    toRoomName: string | null;
    checkInTimestamp: number;
    checkOutTimestamp: number;
}

interface ConflictSuggestion {
    roomInfoId: number;
    roomName: string | null;
    conflictingReservationIds: number[];
    chain: ChainMove[] | null;
    unresolvedReason: string | null;
}

const UNRESOLVED_LABELS: Record<string, string> = {
    NO_OTHER_ROOMS_IN_TYPE: translate('No other rooms of this type to redistribute into'),
    BLOCKED_BY_PINNED: translate('Blocked by a checked-in, condo, or long-stay reservation that cannot be moved automatically'),
    INSUFFICIENT_CAPACITY: translate('Not enough rooms of this type to fit everyone - real overbooking'),
    CHAIN_TOO_LONG: translate('Would require moving too many reservations - resolve manually'),
};

// Generalizacija hasSameRoomOverlap (reservationsTimeline.tsx) preko cijelog store-a:
// broji sobe koje trenutno imaju preklapajuće aktivne rezervacije gdje je barem jedna
// providerReservation (CHM-uzrokovano preklapanje) - isti scope kao backend findConflictedRooms.
function countOverlappingRooms(reservations: ReservationModel[]): number {
    const byRoom = new Map<number, ReservationModel[]>();
    reservations.forEach((r: any) => {
        if (!r.roomInfoId || !ACTIVE_STATUSES.includes(Number(r.statusEnum))) return;
        const list = byRoom.get(r.roomInfoId) || [];
        list.push(r);
        byRoom.set(r.roomInfoId, list);
    });

    let count = 0;
    byRoom.forEach((list: any[]) => {
        if (list.length < 2) return;
        for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
                const a = list[i];
                const b = list[j];
                if (
                    a.checkInTimestamp < b.checkOutTimestamp &&
                    b.checkInTimestamp < a.checkOutTimestamp &&
                    (a.providerReservation || b.providerReservation)
                ) {
                    count++;
                    return;
                }
            }
        }
    });
    return count;
}

function RoomOverlapButton() {
    const reservations = useStore((state: any) => state.model.Reservation) || [];
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [applyingAll, setApplyingAll] = useState(false);
    const [suggestions, setSuggestions] = useState<ConflictSuggestion[] | null>(null);

    // Nudi automatsku preraspodjelu samo dok je CHM stvarno uključen - svjesna odluka
    // (postojeći sukob time ne nestaje iz baze, samo se prestaje nuditi automatsko
    // rješenje nakon gašenja CHM-a). model.ChannelManagerMap u store-u je snapshot iz
    // trenutka logina, ne živi podatak - dohvaćamo postavku direktno, jednom pri mountu
    // (isti poziv koji već koristi Channel Manager stranica), umjesto da se oslanjamo
    // na potencijalno zastarjelu vrijednost iz store-a.
    const [channelManagerEnabled, setChannelManagerEnabled] = useState(false);
    useEffect(() => {
        let cancelled = false;
        rest('/getGeneralSettingByKey/channelManagement', 'GET')
            .then((setting: any) => {
                if (cancelled) return;
                const parsed = setting?.params ? JSON.parse(setting.params) : null;
                setChannelManagerEnabled(!!parsed?.wuBook?.enabled);
            })
            .catch(() => {
                if (!cancelled) setChannelManagerEnabled(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Bez ijedne providerReservation ne postoji preklapanje koje nas zanima - jeftina
    // provjera (O(n), prekida na prvom pogotku) prije skupljeg grupiranja+usporedbe.
    const conflictCount = useMemo(() => {
        if (!channelManagerEnabled) return 0;
        if (!reservations.some((r: any) => r.providerReservation)) return 0;
        return countOverlappingRooms(reservations);
    }, [reservations, channelManagerEnabled]);

    // Komponenta se ne odmontira kad conflictCount padne na 0 (samo vrati null), pa bi
    // isOpen/suggestions ostali "zamrznuti" iz prošlog puta (npr. ako je primjena pala pa
    // korisnik nije ručno zatvorio modal X-om) - čim slijedeći, potpuno nepovezan sukob
    // dođe, modal bi se odmah pojavio bez klika. Reset na čisto stanje čim nema što riješiti.
    useEffect(() => {
        if (conflictCount === 0) {
            setIsOpen(false);
            setSuggestions(null);
        }
    }, [conflictCount]);

    if (conflictCount === 0) return null;

    const openModal = async () => {
        setIsOpen(true);
        setLoading(true);
        try {
            const result = await rest('/getRoomReassignmentSuggestions', 'GET');
            setSuggestions(Array.isArray(result) ? result : []);
        } catch (err) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    // Primjenjuje SVE rješive prijedloge redom (jedan za drugim, ne paralelno) jednim klikom.
    // Sigurno je i kad su sukobi u istom CHM tipu (rješavanje jednog može usput riješiti ili
    // promijeniti drugi - applyRoomReassignment to ponovnom validacijom prije commita ispravno
    // prepozna i odbije ako treba) i kad su u različitim tipovima (potpuno neovisni, nema
    // interakcije). Nakon što se sve pokuša, lista se uvijek iznova dohvaća s backenda
    // (ispod) umjesto lokalnog filtriranja, jer stara kopija više nije pouzdana.
    const applyAll = async () => {
        if (!suggestions) return;
        const resolvable = suggestions.filter((s) => s.chain && s.chain.length > 0);
        if (resolvable.length === 0) return;

        setApplyingAll(true);
        for (const suggestion of resolvable) {
            try {
                await rest('/applyRoomReassignment', 'POST', {
                    moves: (suggestion.chain as ChainMove[]).map((m) => ({
                        reservationId: m.reservationId,
                        toRoomInfoId: m.toRoomInfoId,
                    })),
                });
            } catch (err) {
                // rest() već prikazuje toast s greškom - nastavljamo s ostalima, stvarno
                // stanje provjeravamo ispod svježim dohvatom (primjena jednog prijedloga
                // može usput riješiti ili promijeniti drugi, pa lokalna kopija liste više
                // nije pouzdana).
            }
        }

        try {
            const result = await rest('/getRoomReassignmentSuggestions', 'GET');
            const fresh = Array.isArray(result) ? result : [];
            setSuggestions(fresh);
            if (fresh.length === 0) {
                setIsOpen(false);
            }
        } catch (err) {
            setSuggestions([]);
        } finally {
            setApplyingAll(false);
        }
    };

    return (
        <>
            <div className="button-warning" onClick={openModal}>
                {translate('Check for overlaps')} ({conflictCount})
            </div>
            <InlineModal show={isOpen} setShow={() => setIsOpen(false)} header={translate('Room overlaps')} width="mg">
                {loading ? (
                    <div>{translate('Loading')}</div>
                ) : !suggestions || suggestions.length === 0 ? (
                    <div>{translate('No overlaps found')}</div>
                ) : (
                    <>
                        {suggestions.map((s, index) => (
                            <div key={s.roomInfoId + '-' + index} className="room-overlap-suggestion-card">
                                <h4>
                                    {translate('Room')} {s.roomName || s.roomInfoId}
                                </h4>
                                {s.unresolvedReason ? (
                                    <p>{UNRESOLVED_LABELS[s.unresolvedReason] || s.unresolvedReason}</p>
                                ) : (
                                    <ul>
                                        {s.chain?.map((move) => (
                                            <li key={move.reservationId}>
                                                {move.guestName}: {translate('Room')}{' '}
                                                {move.fromRoomName || move.fromRoomInfoId} &rarr;{' '}
                                                {move.toRoomName || move.toRoomInfoId}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                        {suggestions.some((s) => s.chain && s.chain.length > 0) && (
                            <div className="flex-center-center">
                                <div className="button-primary" onClick={applyAll}>
                                    {applyingAll ? translate('Applying') : translate('Apply all')}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </InlineModal>
        </>
    );
}

export default RoomOverlapButton;
